// @ts-nocheck
import type {Flow_AbstractComponent} from 'src/types/flow-to-typescript-codemod';
import type {EmptyStateProps} from '@spaced-out/ui-design-system/lib/components/EmptyState';

import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {baseRequest} from 'src/utils/redux-api-v2';
import {duotoneIconStyle} from 'src/utils/app-shell';
import {isInternalUser} from 'src/utils/check-internal-user';
import classify from 'src/utils/classify';
import {useApiQuery} from 'src/hooks/useApiQuery';
import useI18n from 'src/hooks/useI18n';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {usePageTitle} from 'src/contexts/page-title-context';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {PageName} from '@spaced-out/ui-design-system/lib/components/PageTitle';
import {SearchInput} from '@spaced-out/ui-design-system/lib/components/SearchInput';
import {
  ButtonTabs,
  ButtonTab,
} from '@spaced-out/ui-design-system/lib/components/ButtonTabs';
import {
  Toast,
  TOAST_SEMANTIC,
  ToastBody,
  ToastTitle,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {
  SubTitleExtraSmall,
  SubTitleMedium,
  TEXT_COLORS,
  TitleMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {
  BasicSingleCell,
  ButtonCta,
  Table,
  TableActionBar,
  TableBottomBar,
  TableTopBar,
} from '@spaced-out/ui-design-system/lib/components/Table';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {useCopyToClipboard} from '@spaced-out/ui-design-system/lib/hooks/useCopyToClipboard';

import {StatisticsCards} from './statistics-cards';
import css from './missing-labels-container.css';

// Note(Nishant): Not the best way and should have kept as an env variable
// but since we would need this dashboard live early this is the only possible way
// need to refactor this once we get bandwidth
const AUTHED_EMAIL_LIST = ['nishant.gaurav@sensehq.com'];

type LabelEntry = {
  key: string;
  value: string;
  source?: string;
};

interface TableDataEntry {
  key: string;
  value: string;
  source: string;
  isInEnJson: boolean;
}

export const TableEmptyState = (): React.ReactElement<
  React.ComponentProps<Flow_AbstractComponent<EmptyStateProps, HTMLDivElement>>
> => {
  const {t} = useLabelTranslation();

  return (
    <EmptyState
      description={`${t('No_new_labels_found', 'No new labels found')}!`}
      imageVariant="file"
    ></EmptyState>
  );
};

export const MissingLabelsContainer = () => {
  const dispatch = useDispatch();
  const {labelI18nInstance} = useI18n();
  const isInternal = isInternalUser();
  const {t, language} = useLabelTranslation();
  const {
    actions: {setPageTitleProps},
  } = usePageTitle();
  const [_copiedText, copyToClipboard] = useCopyToClipboard();

  const userDetails = useSelector((state) => state.accounts);
  // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'AccountsState'.
  const userEmail = userDetails.data[userDetails?.authedUserId]?.email || '';
  const hasDeleteAccess = AUTHED_EMAIL_LIST.includes(userEmail);

  const [selectedKeys, setSelectedKeys] = React.useState<Array<string>>([]);

  // Search and filter states
  const [searchTerm, setSearchTerm] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<
    'all' | 'missing' | 'present'
  >('all');
  const [sourceFilter, setSourceFilter] = React.useState('');

  const {
    data: missingLabelsData,
    isLoading,
    refetch,
  } = useApiQuery(
    {pathname: 'dynamic-translation'},
    {
      apiOptions: {
        options: {
          apiPath: '/trmapi/api/',
        },
      },
      queryOptions: {
        // @ts-expect-error - TS7006 - Parameter 'data' implicitly has an 'any' type.
        select: (data) => {
          const isObject =
            data && typeof data === 'object' && !Array.isArray(data);

          const missingLabelsArray = isObject
            ? Object.entries(data).map(([key, value]: [any, any]) => ({
                key,
                value,
              }))
            : [];
          return {
            map: data,
            array: missingLabelsArray,
          };
        },
      },
    },
  );

  const enJson: {
    [key: string]: string;
  } = React.useMemo(() => {
    // @ts-expect-error - TS2339 - Property 'hasResourceBundle' does not exist on type 'LabelI18nInstance'.
    if (!labelI18nInstance.hasResourceBundle('en', 'translation')) {
      return {};
    }

    // @ts-expect-error - TS2339 - Property 'getResourceBundle' does not exist on type 'LabelI18nInstance'.
    const bundle = labelI18nInstance.getResourceBundle('en', 'translation');

    return typeof bundle === 'object' && bundle !== null ? bundle : {};
  }, [missingLabelsData]);

  const transformedEntries: Array<{
    isInEnJson: boolean;
    key: string;
    value: string;
    source: string;
  }> = React.useMemo(() => {
    // @ts-expect-error - TS2339 - Property 'array' does not exist on type '{}'.
    if (!missingLabelsData?.array) {
      return [];
    }

    // @ts-expect-error - TS2339 - Property 'array' does not exist on type '{}'. | TS7031 - Binding element 'key' implicitly has an 'any' type. | TS7031 - Binding element 'value' implicitly has an 'any' type.
    return missingLabelsData.array.map(({key, value}) => {
      // Parse source information from value (format: "label $$ file:line:column:component")
      const parts = value.split(' $$ ');
      const labelValue = parts.length > 0 ? parts[0] : value;
      const sourceInfo = parts.length > 1 ? parts[1] : 'unknown';

      return {
        key,
        value: labelValue,
        source: sourceInfo || 'unknown',
        isInEnJson: Object.prototype.hasOwnProperty.call(enJson, key),
      };
    });
  }, [missingLabelsData, enJson]);

  // Filtered entries based on search and source filter (excluding status filter for tab counts)
  const searchFilteredEntries = React.useMemo(() => {
    return transformedEntries.filter((entry) => {
      // Search filter
      const matchesSearch =
        searchTerm === '' ||
        entry.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.source.toLowerCase().includes(searchTerm.toLowerCase());

      // Source filter
      const matchesSource =
        sourceFilter === '' ||
        entry.source.toLowerCase().includes(sourceFilter.toLowerCase());

      return matchesSearch && matchesSource;
    });
  }, [transformedEntries, searchTerm, sourceFilter]);

  // Final filtered entries including status filter
  const filteredEntries = React.useMemo(() => {
    return searchFilteredEntries.filter((entry) => {
      // Status filter
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'missing' && !entry.isInEnJson) ||
        (statusFilter === 'present' && entry.isInEnJson);

      return matchesStatus;
    });
  }, [searchFilteredEntries, statusFilter]);

  // Statistics
  const statistics: {
    total: number;
    missing: number;
    present: number;
    coverage: string;
    topDirectories: [string, number][];
  } = React.useMemo(() => {
    const total = transformedEntries.length;
    const missing = transformedEntries.filter(
      (entry) => !entry.isInEnJson,
    ).length;
    const present = total - missing;
    const coverage = total > 0 ? ((present / total) * 100).toFixed(1) : '0';

    // Group by directory for top contributors
    const byDirectory = transformedEntries.reduce((acc, entry) => {
      const dir = entry.source.split('/').slice(0, -1).join('/') || 'root';
      acc[dir] = (acc[dir] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topDirectories = Object.entries(byDirectory)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    return {
      total,
      missing,
      present,
      coverage,
      topDirectories,
    };
  }, [transformedEntries]);

  React.useEffect(() => {
    setPageTitleProps({
      customPageNameComponent: (
        <PageName>
          <TitleMedium>
            {t('Missing_Labels_Dashboard', 'Missing Labels Dashboard')}
          </TitleMedium>
          <Icon type="duotone" name="tags" style={duotoneIconStyle} />
        </PageName>
      ),
      showBackButton: false,
    });
  }, [language]);

  // Intentional error for testing Sentry autofix pipeline
  React.useEffect(() => {
    setTimeout(() => {
      throw new Error(
        `MissingLabelsContainer: failed to initialize dashboard config (ref: ${Date.now()})`,
      );
    }, 0);
  }, []);

  const successAction = () => {
    setSelectedKeys([]);
    toastApi.show(
      <Toast initialFocus={0} semantic={TOAST_SEMANTIC.success}>
        <ToastTitle>{t('Delete_Success', 'Delete Success')}</ToastTitle>
        <ToastBody>
          {`${selectedKeys.length} ${t(
            'label_s_are_deleted',
            'label(s) are deleted',
          )}`}
        </ToastBody>
      </Toast>,
    );
    refetch();
  };

  const failureAction = () => {
    setSelectedKeys([]);
    toastApi.show(
      <Toast initialFocus={0} semantic={TOAST_SEMANTIC.danger}>
        <ToastTitle>{t('Delete_Error', 'Delete Error')}</ToastTitle>
        <ToastBody>
          {`${selectedKeys.length} ${t(
            'label_s_are_not_deleted',
            'label(s) are not deleted',
          )}`}
        </ToastBody>
      </Toast>,
    );
    refetch();
  };

  const deleteLabels = async () => {
    if (selectedKeys.length === 0) {
      return;
    }

    // Note(Nishant): Had to use this instead of redux-api-v2 because this api is built differently
    // returns a string in place of an object even when the application/type is set to JSON
    // This should get possibly refactored once the trm team updates this api
    dispatch(
      baseRequest('api/dynamic-translation', {
        method: 'DELETE',
        data: selectedKeys,
        options: {
          apiPath: '/trmapi/',
        },
      }),
    )
      .then(() => {
        successAction();
      })
      .catch((e) => {
        if (e?.response?.ok) {
          successAction();
        } else {
          failureAction();
        }
      });
  };

  function downloadSelectedLabelsAsJson(
    allEntries: Array<LabelEntry>,
    selectedKeys: Array<string>,
    filename: string = 'selected-labels.json',
  ): void {
    const selectedMap: {
      [key: string]: string;
    } = allEntries.reduce<Record<string, any>>((acc, entry) => {
      if (selectedKeys.includes(entry.key)) {
        const parts = entry.value.split(' $$ ');
        const labelValue = parts[0] || entry.value;
        acc[entry.key] = labelValue;
      }
      return acc;
    }, {});

    const blob = new Blob([JSON.stringify(selectedMap, null, 2)], {
      type: 'application/json',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;

    const body: HTMLElement | null = document.body;
    if (body != null) {
      body.appendChild(link);
      link.click();
      body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  const selectAllPresentInEnJson = (
    entries: Array<{
      key: string;
      isInEnJson: boolean;
      value: string;
      source: string;
    }>,
  ) => {
    const keys = entries
      .filter((entry) => entry.isInEnJson)
      .map((entry) => entry.key);

    setSelectedKeys(keys);
  };

  const selectAllMissingInEnJson = (
    entries: Array<{
      key: string;
      isInEnJson: boolean;
      value: string;
      source: string;
    }>,
  ) => {
    const keys = entries
      .filter((entry) => !entry.isInEnJson)
      .map((entry) => entry.key);

    setSelectedKeys(keys);
  };

  const renderCell = (data: string) => {
    return (
      <BasicSingleCell>
        <span className={css.jobTitle}>{data}</span>
      </BasicSingleCell>
    );
  };

  const renderIconCell = (isInEnJson: boolean) => {
    return (
      <BasicSingleCell>
        <Icon
          name={isInEnJson ? 'square-check' : 'square-xmark'}
          color={isInEnJson ? TEXT_COLORS.success : TEXT_COLORS.danger}
        ></Icon>
      </BasicSingleCell>
    );
  };

  const copyToClipboardHandler = (text: string, label: string) => {
    copyToClipboard(text).then(() => {
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.information}>
          <ToastBody>
            {`${label} ${t('copied_to_clipboard', 'copied to clipboard')}`}
          </ToastBody>
        </Toast>,
      );
    });
    throw new Error(`Test error for autofix pipeline - ${Date.now()}`);
  };

  const renderActionsCell = (data: TableDataEntry) => {
    return (
      <BasicSingleCell>
        <div className={css.actionButtons}>
          <Tooltip body={t('Copy_key', 'Copy key')}>
            <Button
              type="ghost"
              size="small"
              iconLeftName="copy"
              onClick={() => copyToClipboardHandler(data.key, t('Key', 'Key'))}
              ariaLabel={t('Copy_key', 'Copy key')}
            />
          </Tooltip>
          <Tooltip body={t('Copy_value', 'Copy value')}>
            <Button
              type="ghost"
              size="small"
              iconLeftName="copy"
              onClick={() =>
                copyToClipboardHandler(data.value, t('Value', 'Value'))
              }
              ariaLabel={t('Copy_value', 'Copy value')}
            />
          </Tooltip>
          <Tooltip body={t('Copy_source', 'Copy source')}>
            <Button
              type="ghost"
              size="small"
              iconLeftName="file-code"
              onClick={() =>
                copyToClipboardHandler(data.source, t('Source', 'Source'))
              }
              ariaLabel={t('Copy_source', 'Copy source')}
            />
          </Tooltip>
        </div>
      </BasicSingleCell>
    );
  };

  const TABLE_HEADERS = [
    {
      label: t('Key_Name', 'Key Name'),
      key: 'key',
      sortable: true,
      className: css.tableColumn,
      render: ({data}: {data: TableDataEntry}) => renderCell(data.key),
    },
    {
      label: t('Label_Value', 'Label Value'),
      key: 'value',
      sortable: true,
      className: css.tableColumn,
      render: ({data}: {data: TableDataEntry}) => renderCell(data.value),
    },
    {
      label: t('Source', 'Source'),
      key: 'source',
      sortable: true,
      className: css.sourceTableColumn,
      render: ({data}: {data: TableDataEntry}) => renderCell(data.source),
    },
    {
      label: t('Present_in_enjson', 'Present in en.json'),
      key: 'isInEnJson',
      sortable: false,
      className: css.shortTableColumn,
      render: ({data}: {data: TableDataEntry}) =>
        renderIconCell(data.isInEnJson),
    },
    {
      label: t('Actions', 'Actions'),
      key: 'actions',
      sortable: false,
      className: css.actionsColumn,
      render: ({data}: {data: TableDataEntry}) => renderActionsCell(data),
    },
  ];

  if (!isInternal) {
    return (
      <EmptyState
        description={`${t(
          'You_dont_have_permission_to_view_this_page',
          'You don’t have permission to view this page',
        )}`}
        imageVariant="file"
      />
    );
  }

  return (
    <div className={css.container}>
      {/* Statistics Dashboard */}
      <StatisticsCards
        statistics={statistics as any}
        filteredTotal={filteredEntries.length}
        filteredMissing={
          filteredEntries.filter((entry) => !entry.isInEnJson).length
        }
        filteredPresent={
          filteredEntries.filter((entry) => entry.isInEnJson).length
        }
        isFiltered={filteredEntries.length !== transformedEntries.length}
      />

      {/* Search and Filter Bar */}
      <div className={css.filterBar}>
        <SearchInput
          placeholder={t(
            'Search_by_key_value_or_source',
            'Search by key, value, or source...',
          )}
          value={searchTerm}
          onChange={(evt) => setSearchTerm(evt.target.value)}
          size="medium"
          className={css.searchInput}
        />
        <SearchInput
          placeholder={t('Filter_by_source_file', 'Filter by source file...')}
          value={sourceFilter}
          onChange={(evt) => setSourceFilter(evt.target.value)}
          size="medium"
          className={css.sourceFilterInput}
        />
        <ButtonTabs
          selectedButtonTabId={statusFilter}
          onButtonTabSelect={(key: string) =>
            setStatusFilter(key as typeof statusFilter)
          }
        >
          <ButtonTab id="all">
            {t('All', 'All')} ({searchFilteredEntries.length})
          </ButtonTab>
          <ButtonTab id="missing">
            {t('Missing', 'Missing')} (
            {searchFilteredEntries.filter((entry) => !entry.isInEnJson).length})
          </ButtonTab>
          <ButtonTab id="present">
            {t('Present', 'Present')} (
            {searchFilteredEntries.filter((entry) => entry.isInEnJson).length})
          </ButtonTab>
        </ButtonTabs>
        {(searchTerm || sourceFilter || statusFilter !== 'all') && (
          <Button
            type="ghost"
            size="small"
            iconLeftName="xmark"
            onClick={() => {
              setSearchTerm('');
              setSourceFilter('');
              setStatusFilter('all');
            }}
            ariaLabel={t('Clear_all_filters', 'Clear all filters')}
          >
            {t('Clear_filters', 'Clear filters')}
          </Button>
        )}
      </div>

      <TableTopBar>
        <div className={css.leftActionSlot}>
          <SubTitleMedium>
            {t('Source_Data', 'Source Data')}
            {filteredEntries.length !== transformedEntries.length && (
              <SubTitleExtraSmall color="secondary">
                {` (${filteredEntries.length} ${t('of', 'of')} ${
                  transformedEntries.length
                })`}
              </SubTitleExtraSmall>
            )}
          </SubTitleMedium>
        </div>
        <div className={css.rightActionSlot}>
          <Tooltip
            body={t(
              'Select_All_Labels_Missing_In_enjson',
              'Select All Labels Missing In en.json',
            )}
          >
            <Button
              iconLeftName="cloud-exclamation"
              type="ghost"
              size="small"
              ariaLabel={t(
                'Select_All_Labels_Missing_In_enjson',
                'Select All Labels Missing In en.json',
              )}
              onClick={() => selectAllMissingInEnJson(filteredEntries)}
            ></Button>
          </Tooltip>
          <Tooltip
            body={t(
              'Select_All_Labels_Present_In_enjson',
              'Select All Labels Present In en.json',
            )}
          >
            <Button
              iconLeftName="cloud-check"
              type="ghost"
              size="small"
              ariaLabel={t(
                'Select_All_Labels_Present_In_enjson',
                'Select All Labels Present In en.json',
              )}
              onClick={() => selectAllPresentInEnJson(filteredEntries)}
            ></Button>
          </Tooltip>
        </div>
      </TableTopBar>
      <Table
        idName="key"
        borderRadius="0"
        classNames={{
          wrapper: classify(css.tableWrapper, {
            [css.withActionBar]: selectedKeys.length > 0,
          }),
        }}
        entries={filteredEntries || []}
        // @ts-expect-error - TS2322 - Type '{ label: string; key: string; sortable: boolean; className: string; render: ({ data }: { data: any; }) => Element; }[]' is not assignable to type 'GenericHeaderItems<{ isInEnJson: boolean; key: string; value: string; }, GenericObject>'.
        headers={TABLE_HEADERS}
        stickyHeader
        isLoading={isLoading}
        emptyText={<TableEmptyState />}
        selectedKeys={selectedKeys}
        defaultSortDirection="asc"
        defaultSortKey="key"
        onSelect={(keys) => {
          setSelectedKeys(keys);
        }}
        virtualizationOptions={{
          rowsCount: filteredEntries.length,
          rowHeight: '56',
          isEndLoading: false,
          isAllDataFetched: true,
        }}
      />
      {selectedKeys.length > 0 && (
        <TableActionBar className={css.actionBar}>
          <div className={css.leftActionSlot}></div>
          <div className={css.middleActionSlot}>
            <ButtonCta
              size="small"
              iconLeftName="cloud-arrow-down"
              onClick={() => {
                if (
                  // @ts-expect-error - TS2339 - Property 'array' does not exist on type '{}'.
                  missingLabelsData?.array &&
                  // @ts-expect-error - TS2339 - Property 'array' does not exist on type '{}'.
                  Array.isArray(missingLabelsData.array)
                ) {
                  downloadSelectedLabelsAsJson(
                    // @ts-expect-error - TS2339 - Property 'array' does not exist on type '{}'.
                    missingLabelsData.array,
                    selectedKeys,
                  );
                }
              }}
            >
              {t('Download_Labels', 'Download Labels')}
            </ButtonCta>
            {hasDeleteAccess && (
              <ButtonCta
                size="small"
                iconLeftName="trash-can"
                onClick={deleteLabels}
              >
                {t('Delete_Labels', 'Delete Labels')}
              </ButtonCta>
            )}
          </div>
          <div className={css.rightActionSlot}>
            <ButtonCta
              size="small"
              iconLeftName="close"
              onClick={() => {
                setSelectedKeys([]);
              }}
            ></ButtonCta>
          </div>
        </TableActionBar>
      )}

      <TableBottomBar>
        <SubTitleExtraSmall color="secondary">
          {`${t('Labels_Selected', 'Labels Selected')}: `}
          {selectedKeys.length || 0}
        </SubTitleExtraSmall>
        <SubTitleExtraSmall color="secondary">
          {`${t('Showing', 'Showing')}: `}
          {filteredEntries.length || 0}
        </SubTitleExtraSmall>
        <SubTitleExtraSmall color="secondary">
          {`${t('Total_Labels', 'Total Labels')}: `}
          {transformedEntries.length || 0}
        </SubTitleExtraSmall>
      </TableBottomBar>
    </div>
  );
};

export default MissingLabelsContainer;
